import { Controller } from '@hotwired/stimulus'

// app/javascript/controllers/base_3d_controller.js
export default class extends Controller {
  static targets = [
    'scene', 'boxContainer', 'box',
    'frontFace', 'frontImage',
    'uploadButton', 'imageUpload',
  ]

  static values = {
    rotateX: { type: Number, default: 0 },
    rotateY: { type: Number, default: 0 },
    zoomLevel: { type: Number, default: 150 },
    boxWidth: { type: Number, default: 200 },
    boxHeight: { type: Number, default: 200 },
    boxDepth: { type: Number, default: 50 },
    maxRotation: { type: Number, default: 45 },
    perspective: { type: Number, default: 1500 },
  }

  initialize() {
    // Bind methods to preserve 'this' context
    this.rotateStart = this.rotateStart.bind(this)
    this.rotate = this.rotate.bind(this)
    this.rotateEnd = this.rotateEnd.bind(this)
    this.handleImageUpload = this.handleImageUpload.bind(this)
    this.initialBoxSize = { width: this.boxWidthValue, height: this.boxHeightValue }
  }

  connect() {
    // Initialize properties
    this.isDragging = false
    this.startX = 0
    this.startY = 0
    this.currentRotateX = this.rotateXValue
    this.currentRotateY = this.rotateYValue

    // Initialize box dimensions
    // this.updateBoxDimensions(this.boxWidthValue, this.boxHeightValue)

    // Set the initial rotation
    this.resetRotation()

    // Update perspective
    this.updatePerspective(this.perspectiveValue)

    // Setup event listeners
    this.setupEventListeners()

    // Call child-specific connect logic
    this.childConnect()
  }

  disconnect() {
    this.removeEventListeners()
    this.childDisconnect()
  }

  // Override this in child classes for specific connect logic
  childConnect() {}

  // Override this in child classes for specific disconnect logic
  childDisconnect() {}

  setupEventListeners() {
    // Rotation events
    if (this.hasSceneTarget) {
      this.sceneTarget.addEventListener('mousedown', this.rotateStart)
      this.sceneTarget.addEventListener('touchstart', this.rotateStart, { passive: false })
    }
    else if (this.element) {
      // Fallback to element if no scene target (for metal controller)
      this.element.addEventListener('mousedown', this.rotateStart)
      this.element.addEventListener('touchstart', this.rotateStart, { passive: false })
    }

    document.addEventListener('mouseup', this.rotateEnd)
    document.addEventListener('touchend', this.rotateEnd)
    document.addEventListener('mousemove', this.rotate)
    document.addEventListener('touchmove', this.rotate, { passive: false })

    // Upload button click
    if (this.hasUploadButtonTarget && this.hasImageUploadTarget) {
      this.uploadButtonTarget.addEventListener('click', () => {
        this.imageUploadTarget.click()
      })
      this.imageUploadTarget.addEventListener('change', this.handleImageUpload)
    }
  }

  removeEventListeners() {
    // Rotation events
    if (this.hasSceneTarget) {
      this.sceneTarget.removeEventListener('mousedown', this.rotateStart)
      this.sceneTarget.removeEventListener('touchstart', this.rotateStart)
    }
    else if (this.element) {
      this.element.removeEventListener('mousedown', this.rotateStart)
      this.element.removeEventListener('touchstart', this.rotateStart)
    }

    document.removeEventListener('mouseup', this.rotateEnd)
    document.removeEventListener('touchend', this.rotateEnd)
    document.removeEventListener('mousemove', this.rotate)
    document.removeEventListener('touchmove', this.rotate)

    // Upload events
    if (this.hasUploadButtonTarget && this.hasImageUploadTarget) {
      this.uploadButtonTarget.removeEventListener('click', () => {
        this.imageUploadTarget.click()
      })
      this.imageUploadTarget.removeEventListener('change', this.handleImageUpload)
    }
  }

  // Rotation methods
  rotateStart(e) {
    // Only allow rotation when it's not a two-finger pinch
    if (e.touches && e.touches.length > 1) return

    this.isDragging = true

    // Save the starting position
    if (e.type === 'touchstart') {
      this.startX = e.touches[0].clientX
      this.startY = e.touches[0].clientY
    }
    else {
      this.startX = e.clientX
      this.startY = e.clientY
    }

    // Save current rotation as starting point for this drag
    this.currentRotateX = this.rotateXValue
    this.currentRotateY = this.rotateYValue

    e.preventDefault()

    // Set cursor if element supports it
    if (this.element.style) {
      this.element.style.cursor = 'grabbing'
    }
  }

  rotate(e) {
    // Skip if it's a pinch gesture
    if (e.touches && e.touches.length > 1) return

    if (this.isDragging) {
      e.preventDefault()

      let clientX, clientY

      if (e.type === 'touchmove') {
        clientX = e.touches[0].clientX
        clientY = e.touches[0].clientY
      }
      else {
        clientX = e.clientX
        clientY = e.clientY
      }

      // Calculate how much the mouse/touch has moved
      const deltaX = clientX - this.startX
      const deltaY = clientY - this.startY

      // Convert the movement to rotation (invert Y for natural feel)
      const sensitivity = 5
      this.rotateXValue = this.currentRotateX - deltaY / sensitivity
      this.rotateYValue = this.currentRotateY + deltaX / sensitivity

      // Limit rotation
      this.rotateXValue = Math.max(-this.maxRotationValue, Math.min(this.maxRotationValue, this.rotateXValue))
      this.rotateYValue = Math.max(-this.maxRotationValue, Math.min(this.maxRotationValue, this.rotateYValue))

      // Update the rotation
      this.updateRotation()
    }
  }

  rotateEnd() {
    this.isDragging = false
    if (this.element.style) {
      this.element.style.cursor = 'grab'
    }
  }

  // Update rotation when value changes
  rotateXValueChanged() {
    this.updateRotation()
  }

  rotateYValueChanged() {
    this.updateRotation()
  }

  // Override this in child classes - different controllers have different rotation targets
  updateRotation() {
    if (this.hasBoxContainerTarget) {
      this.boxContainerTarget.style.transform = `translate(-50%, -50%) rotateX(${this.rotateXValue}deg) rotateY(${this.rotateYValue}deg)`
    }
  }

  resetRotation() {
    this.rotateXValue = 0
    this.rotateYValue = 0
    this.updateRotation()
  }

  // Box dimensions methods
  updateBoxDimensions(width, height) {
    this.initialBoxSize = {
      width: width,
      height: height,
    }

    const zoomFactor = this.zoomLevelValue / 100
    this.boxWidthValue = this.initialBoxSize.width * zoomFactor
    this.boxHeightValue = this.initialBoxSize.height * zoomFactor

    // Call child-specific dimension update
    this.updateChildDimensions()
  }

  // Override this in child classes for specific dimension updates
  updateChildDimensions() {}

  // Update the scene perspective
  updatePerspective(perspective) {
    if (this.hasSceneTarget) {
      this.sceneTarget.style.perspective = `${perspective}px`
    }
  }

  perspectiveValueChanged() {
    this.updatePerspective(this.perspectiveValue)
  }

  // Utility method for pinch gestures
  getPinchDistance(e) {
    const dx = e.touches[0].clientX - e.touches[1].clientX
    const dy = e.touches[0].clientY - e.touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  // Image upload methods
  handleImageUpload(e) {
    const file = e.target.files[0]
    if (!file) return

    if (!file.type.match('image.*')) {
      this.handleImageError('Please select an image file')
      return
    }

    const reader = new FileReader()
    reader.onload = (event) => {
      const imageUrl = event.target.result

      // Create a temporary image to get dimensions
      const img = new Image()
      img.onload = () => {
        // Calculate new box dimensions based on image aspect ratio
        let newWidth, newHeight

        // Maintain aspect ratio, but limit size
        const maxDimension = 300
        if (img.width > img.height) {
          newWidth = Math.min(img.width, maxDimension)
          newHeight = (img.height / img.width) * newWidth
        }
        else {
          newHeight = Math.min(img.height, maxDimension)
          newWidth = (img.width / img.height) * newHeight
        }

        // Update box dimensions
        this.updateBoxDimensions(newWidth, newHeight)

        // Set the image on the front face
        if (this.hasFrontImageTarget) {
          this.frontImageTarget.src = imageUrl
          this.frontImageTarget.style.display = 'block'
        }

        // Call child-specific image handling
        this.handleChildImageLoad(imageUrl, file)
      }
      img.src = imageUrl
    }

    reader.readAsDataURL(file)
  }

  // Override in child classes for specific image handling
  // eslint-disable-next-line no-unused-vars
  handleChildImageLoad(imageUrl, file) {}

  // Override in child classes for specific error handling
  handleImageError(message) {
    console.error(message)
  }
}
